import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useAssistantIntegration} from '../AssistantIntegration';
import {handleResponse} from './http';

export type ToolAuthMode =
    | 'oauthPerUser'
    | 'oauthShared'
    | 'staticAdmin'
    | 'staticPerUser';

export interface ToolAuthConnectionStatus {
    connectionId: string;
    name: string;
    description?: string | null;
    mode: ToolAuthMode;
    providerType: 'other' | 'microsoft' | 'google';
    serverNames: string[];
    isAuthenticated: boolean;
    isExpired: boolean;
    expiresAt?: string | null;
    hasRefreshToken: boolean;
    requiresUserAction: boolean;
}

export interface ToolAuthStatus {
    hasToolsToConfigure: boolean;
    requiresUserAction: boolean;
    connections: ToolAuthConnectionStatus[];
}

export const toolAuthStatusKey = (assistantKeyOrId: string) =>
    ['tool-auth-status', assistantKeyOrId] as const;

export function useToolAuthStatus(assistantKeyOrId: string | undefined) {
    const {authenticatedFetch, baseUrl} = useAssistantIntegration();

    return useQuery({
        queryKey: toolAuthStatusKey(assistantKeyOrId ?? ''),
        enabled: !!assistantKeyOrId,
        queryFn: async () => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/assistants/${encodeURIComponent(assistantKeyOrId!)}/tool-auth`
            );
            return handleResponse<ToolAuthStatus>(response);
        }
    });
}

export function useStartToolAuth(assistantKeyOrId: string) {
    const {authenticatedFetch, baseUrl} = useAssistantIntegration();

    return useMutation({
        mutationFn: async (connectionId: string) => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/assistants/${encodeURIComponent(assistantKeyOrId)}/tool-auth/${connectionId}/start`,
                {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: '{}'
                }
            );
            return handleResponse<{url: string}>(response);
        }
    });
}

export function useProvideToolAuthKey(assistantKeyOrId: string) {
    const {authenticatedFetch, baseUrl} = useAssistantIntegration();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            connectionId,
            key
        }: {
            connectionId: string;
            key: string;
        }) => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/assistants/${encodeURIComponent(assistantKeyOrId)}/tool-auth/${connectionId}/key`,
                {
                    method: 'PUT',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({key})
                }
            );
            return handleResponse<void>(response);
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: toolAuthStatusKey(assistantKeyOrId)
            });
        }
    });
}

export function useDisconnectToolAuth(assistantKeyOrId: string) {
    const {authenticatedFetch, baseUrl} = useAssistantIntegration();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (connectionId: string) => {
            const response = await authenticatedFetch(
                `${baseUrl}/api/tool-auth/${connectionId}`,
                {method: 'DELETE'}
            );
            return handleResponse<void>(response);
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: toolAuthStatusKey(assistantKeyOrId)
            });
        }
    });
}

/**
 * Opens the backend-built authorization URL in a popup and resolves when the window closes -
 * closure itself is the completion signal (no cross-window messaging). Falls back to a
 * full-page navigation when the popup is blocked or on small screens.
 */
export function openAuthPopup(url: string): Promise<void> {
    const width = 600;
    const height = 720;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
        url,
        'certavue-connection-auth',
        `popup=yes,width=${width},height=${height},left=${left},top=${top}`
    );

    if (!popup) {
        window.location.assign(url);
        return Promise.resolve();
    }

    return new Promise(resolve => {
        const timer = window.setInterval(() => {
            if (popup.closed) {
                window.clearInterval(timer);
                resolve();
            }
        }, 1000);
    });
}
